import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { ArrowLeft, Edit, Share2, Star, StarOff, Pin, PinOff, Lock } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dialog } from "@/components/ui/dialog";
import { AccessLogs } from "@/components/Entries/AccessLogs";
import { SaveCountDisplay } from "@/components/Entries/SaveCountDisplay";
import { useEntryTracking } from "@/hooks/useEntryTracking";
import { AttachmentsSection } from "@/components/Attachments/AttachmentsSection";

type Entry = Database["public"]["Tables"]["entries"]["Row"] & {
  creator_email?: string;
};

type RelatedEntry = {
  id: string;
  title: string;
  entry_type: string;
};

// Define a type for the entry_type based on the Database type
type EntryType = Database["public"]["Enums"]["entry_type"];

const EntryView = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState<Entry | null>(null);
  const [relatedEntries, setRelatedEntries] = useState<RelatedEntry[]>([]);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCreator, setIsCreator] = useState(false);

  // Use entry tracking hook
  useEntryTracking(id || "");

  useEffect(() => {
    if (!id || !user) return;

    const fetchEntry = async () => {
      const { data, error } = await supabase
        .from("entries")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        toast.error("Failed to load entry");
        navigate("/dashboard");
        return;
      }

      // Get creator email from auth system if it's the current user
      let creatorEmail = "Unknown";
      if (user.id === data.creator_id) {
        creatorEmail = user.email || "Unknown";
      }

      const entryWithEmail = {
        ...data,
        creator_email: creatorEmail,
      };

      setEntry(entryWithEmail);
      setIsCreator(user.id === data.creator_id);

      // If the entry has a password and the user is not the creator,
      // show the password dialog
      if (data.password && !isAuthenticated && user.id !== data.creator_id) {
        setPasswordDialogOpen(true);
      } else {
        setIsAuthenticated(true);
        // Fetch related entries
        if (data.entry_type) {
          fetchRelatedEntries(data.id, data.entry_type);
        }
      }

      setLoading(false);
    };

    fetchEntry();
  }, [id, navigate, user, isAuthenticated]);

  const fetchRelatedEntries = async (entryId: string, entryType: EntryType) => {
    // Find entries with the same type, excluding the current entry
    const { data, error } = await supabase
      .from("entries")
      .select("id, title, entry_type")
      .eq("entry_type", entryType)
      .neq("id", entryId)
      .limit(3);

    if (error) {
      console.error("Error fetching related entries:", error);
      return;
    }

    setRelatedEntries(data || []);
  };

  const verifyPassword = () => {
    if (!entry) return;
    
    if (passwordInput === entry.password) {
      setIsAuthenticated(true);
      setPasswordDialogOpen(false);
      if (entry.entry_type) {
        fetchRelatedEntries(entry.id, entry.entry_type);
      }
    } else {
      toast.error("Incorrect password");
    }
  };

  const toggleFavorite = async () => {
    if (!entry) return;
    
    try {
      const { error } = await supabase
        .from("entries")
        .update({ is_favorite: !entry.is_favorite })
        .eq("id", entry.id);

      if (error) throw error;
      
      setEntry({ ...entry, is_favorite: !entry.is_favorite });
      
      toast.success(entry.is_favorite ? 
        "Removed from favorites" : 
        "Added to favorites"
      );
    } catch (error: any) {
      toast.error("Failed to update favorite status");
      console.error("Error updating favorite:", error);
    }
  };

  const togglePinned = async () => {
    if (!entry) return;
    const isPinned = entry.is_pinned || false;
    
    try {
      const { error } = await supabase
        .from("entries")
        .update({ is_pinned: !isPinned })
        .eq("id", entry.id);

      if (error) throw error;
      
      setEntry({ ...entry, is_pinned: !isPinned });
      
      toast.success(isPinned ? 
        "Entry unpinned" : 
        "Entry pinned to dashboard"
      );
    } catch (error: any) {
      toast.error("Failed to update pinned status");
      console.error("Error updating pinned status:", error);
    }
  };

  const copyShareLink = () => {
    if (!entry) return;
    
    const shareUrl = `${window.location.origin}/shared/${entry.share_id}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied to clipboard");
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mr-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-4 w-32 mb-6" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4 mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xl font-medium mb-2">Entry not found</h3>
        <p className="text-muted-foreground mb-4">
          This entry may have been deleted or you don't have permission to view it
        </p>
        <Button onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Password dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Lock className="h-4 w-4 mr-2" />
              Password Protected Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              This entry is password protected. Please enter the password to view it.
            </p>
            <Input
              type="password"
              placeholder="Password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && verifyPassword()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Cancel
            </Button>
            <Button onClick={verifyPassword}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entry content */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mr-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">{entry.title}</h1>
          {entry.password && <Lock className="h-4 w-4 ml-2 text-muted-foreground" />}
        </div>
        <div className="flex gap-2">
          {isCreator && (
            <Button variant="outline" size="icon" onClick={togglePinned}>
              {entry.is_pinned ? (
                <PinOff className="h-4 w-4" />
              ) : (
                <Pin className="h-4 w-4" />
              )}
            </Button>
          )}
          <Button variant="outline" size="icon" onClick={toggleFavorite}>
            {entry.is_favorite ? (
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            ) : (
              <StarOff className="h-4 w-4" />
            )}
          </Button>
          <Button variant="outline" size="icon" onClick={copyShareLink}>
            <Share2 className="h-4 w-4" />
          </Button>
          {isCreator && (
            <Button onClick={() => navigate(`/dashboard/edit/${entry.id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Badge variant="outline">{entry.entry_type}</Badge>
        {entry.is_public ? (
          <Badge variant="secondary">Public</Badge>
        ) : (
          <Badge variant="outline">Private</Badge>
        )}
        {entry.password && <Badge variant="outline">Password Protected</Badge>}
        <SaveCountDisplay 
          saveCount={entry.save_count || 0} 
          isCreator={isCreator} 
          showInline 
        />
      </div>

      <div className="text-sm text-muted-foreground mb-8 flex flex-wrap gap-x-4 gap-y-1">
        <div>
          Created by {entry.creator_email?.split('@')[0] || "Unknown"}
        </div>
        <div>
          Last updated: {new Date(entry.updated_at).toLocaleDateString()}
          {' • '}
          {new Date(entry.updated_at).toLocaleTimeString()}
        </div>
        <SaveCountDisplay saveCount={entry.save_count || 0} isCreator={isCreator} />
      </div>

      {isAuthenticated ? (
        <>
          <div 
            className="entry-content mb-10"
            dangerouslySetInnerHTML={{ __html: entry.content || "<p><em>No content</em></p>" }}
          />

          {/* New Attachments Section (read-only) */}
          <AttachmentsSection 
            entryId={entry.id} 
            userId={user?.id}
            editable={false}
          />

          {/* Access logs */}
          <AccessLogs 
            entryId={entry.id} 
            isCreator={isCreator} 
            isPublic={entry.is_public || false} 
          />

          {/* Related Entries Section */}
          {relatedEntries.length > 0 && (
            <div className="mt-16">
              <h2 className="text-xl font-semibold mb-4">Related Entries</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {relatedEntries.map((related) => (
                  <Card key={related.id} className="hover:bg-accent/50 transition-colors">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">
                        <Button 
                          variant="link" 
                          className="p-0 h-auto font-semibold"
                          onClick={() => navigate(`/dashboard/entry/${related.id}`)}
                        >
                          {related.title}
                        </Button>
                      </CardTitle>
                      <Badge variant="outline" className="mt-1 w-fit">
                        {related.entry_type}
                      </Badge>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center p-8 border rounded-lg">
          <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">Password Protected Content</h3>
          <p className="text-muted-foreground mb-4">
            This entry is protected. Enter the password to view its contents.
          </p>
          <Button onClick={() => setPasswordDialogOpen(true)}>
            Enter Password
          </Button>
        </div>
      )}
    </div>
  );
};

export default EntryView;
